import React, { useState, useRef } from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import OptimizedImage from '../shared/OptimizedImage';

// Editorial theme colors
const COLORS = {
  cream: '#FAF8F5',
  charcoal: '#2D2A26',
  terracotta: '#C45D3A',
  border: '#2D2A26'
};

const SectionContainer = styled(Box)({
  backgroundColor: COLORS.cream,
  borderBottom: `1px solid ${COLORS.charcoal}`
});

const SectionHeader = styled(Box)({
  padding: '48px 64px 32px',
  borderBottom: `1px solid ${COLORS.charcoal}`,
  '@media (max-width: 768px)': {
    padding: '32px 20px 24px'
  }
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: '42px',
  fontWeight: 700,
  color: COLORS.charcoal,
  letterSpacing: '-1px',
  '@media (max-width: 768px)': {
    fontSize: '32px'
  }
});

const SectionSubtitle = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: COLORS.charcoal,
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '8px'
});

const CarouselContainer = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center'
});

const CarouselTrack = styled(Box)({
  display: 'flex',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none'
});

const CarouselItem = styled(Box)({
  flexShrink: 0,
  width: '400px',
  height: '300px',
  borderRight: `1px solid ${COLORS.charcoal}`,
  scrollSnapAlign: 'start',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  transition: 'opacity 0.3s ease',
  '&:hover': {
    '& .overlay': {
      opacity: 1
    }
  },
  '@media (max-width: 768px)': {
    width: '300px',
    height: '225px'
  }
});

const ImageOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(45, 42, 38, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease'
});

const ViewText = styled(Typography)({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: COLORS.cream,
  textTransform: 'uppercase',
  letterSpacing: '2px',
  padding: '12px 24px',
  border: `1px solid ${COLORS.cream}`
});

const NavButton = styled(IconButton)({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: COLORS.charcoal,
  color: COLORS.cream,
  width: '48px',
  height: '48px',
  borderRadius: 0,
  zIndex: 10,
  '&:hover': {
    backgroundColor: COLORS.terracotta
  },
  '@media (max-width: 768px)': {
    width: '40px',
    height: '40px'
  }
});

const LightboxModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(45, 42, 38, 0.95)'
});

const LightboxContent = styled(Box)({
  position: 'relative',
  maxWidth: '90vw',
  maxHeight: '90vh',
  outline: 'none'
});

const LightboxImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '90vh',
  objectFit: 'contain'
});

const LightboxButton = styled(IconButton)({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: COLORS.cream,
  color: COLORS.charcoal,
  borderRadius: 0,
  '&:hover': {
    backgroundColor: COLORS.terracotta,
    color: COLORS.cream
  }
});

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: '20px',
  right: '20px',
  backgroundColor: COLORS.cream,
  color: COLORS.charcoal,
  borderRadius: 0,
  '&:hover': {
    backgroundColor: COLORS.terracotta,
    color: COLORS.cream
  }
});

const PhotoCounter = styled(Typography)({
  fontFamily: "'Space Mono', monospace",
  fontSize: '14px',
  color: COLORS.cream,
  position: 'absolute',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  letterSpacing: '2px'
});

const EditorialPhotoGallery = ({ photos }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  const displayPhotos = photos?.slice(0, 8) || [];

  if (displayPhotos.length === 0) {
    return null;
  }

  const handlePhotoClick = (photo, index) => {
    setSelectedPhoto(photo);
    setCurrentIndex(index);
  };

  const handleClose = () => {
    setSelectedPhoto(null);
  };

  const handlePrevious = (e) => {
    e?.stopPropagation();
    const newIndex = currentIndex === 0 ? displayPhotos.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedPhoto(displayPhotos[newIndex]);
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    const newIndex = currentIndex === displayPhotos.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedPhoto(displayPhotos[newIndex]);
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <SectionContainer id="gallery">
      <SectionHeader>
        <SectionSubtitle>Visual Stories</SectionSubtitle>
        <SectionTitle>Photo Gallery</SectionTitle>
      </SectionHeader>

      <CarouselContainer>
        <NavButton
          sx={{ left: '16px' }}
          onClick={() => scrollCarousel('left')}
        >
          <NavigateBeforeIcon />
        </NavButton>

        <CarouselTrack ref={carouselRef}>
          {displayPhotos.map((photo, index) => (
            <CarouselItem
              key={photo.id || index}
              onClick={() => handlePhotoClick(photo, index)}
            >
              <OptimizedImage
                src={photo.url}
                alt={photo.name || `Gallery photo ${index + 1}`}
                width="100%"
                height="100%"
                objectFit="cover"
                borderRadius="0"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  filter: 'grayscale(20%)'
                }}
              />
              <ImageOverlay className="overlay">
                <ViewText>View</ViewText>
              </ImageOverlay>
            </CarouselItem>
          ))}
        </CarouselTrack>

        <NavButton
          sx={{ right: '16px' }}
          onClick={() => scrollCarousel('right')}
        >
          <NavigateNextIcon />
        </NavButton>
      </CarouselContainer>

      <LightboxModal open={!!selectedPhoto} onClose={handleClose}>
        <LightboxContent>
          {selectedPhoto && (
            <>
              <LightboxImage
                src={selectedPhoto.url}
                alt={selectedPhoto.name || 'Gallery photo'}
              />
              <CloseButton onClick={handleClose}>
                <CloseIcon />
              </CloseButton>
              <LightboxButton sx={{ left: '20px' }} onClick={handlePrevious}>
                <NavigateBeforeIcon />
              </LightboxButton>
              <LightboxButton sx={{ right: '20px' }} onClick={handleNext}>
                <NavigateNextIcon />
              </LightboxButton>
              <PhotoCounter>
                {String(currentIndex + 1).padStart(2, '0')} / {String(displayPhotos.length).padStart(2, '0')}
              </PhotoCounter>
            </>
          )}
        </LightboxContent>
      </LightboxModal>
    </SectionContainer>
  );
};

export default EditorialPhotoGallery;
